<script lang="ts">
	import { Dropzone } from "flowbite-svelte";
	import { createEventDispatcher } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { UPLOADABLE_IMAGE_FILE_TYPES } from "#lib/components/images/constants.js";

	interface $$Events {
		change: CustomEvent<File>;
		changeMult: CustomEvent<File[]>;
		click: MouseEvent;
	}

	const dispatch = createEventDispatcher();

	export let fileTypes = UPLOADABLE_IMAGE_FILE_TYPES.join(",");
	export let dropzoneClass = "";
	export let lastError: unknown = undefined;
	export let disabled = false;
	export let multiple = false;

	/**
	 * Keep track of drag events. This lets us know if the user is dragging a file over the dropzone or any of its children.
	 */
	let dragCounter = 0;

	function handleDragEnter() {
		dragCounter++;
	}

	function handleDragLeave() {
		dragCounter--;
	}

	function handleDrop(event: DragEvent) {
		event.preventDefault();
		dragCounter = 0;
		handleFiles(event.dataTransfer?.files);
	}

	function handleChange(event: Event) {
		const target = event.target as HTMLInputElement;
		handleFiles(target?.files);
	}

	function handleFiles(dropped: FileList | undefined | null) {
		lastError = undefined;
		if (!dropped) {
			return;
		}
		const files: File[] = [];
		for (let i = 0; i < dropped.length; i++) {
			if (fileTypes.includes(dropped[i].type)) {
				files.push(dropped[i]);
			}
		}
		if (files.length > 0) {
			if (multiple) {
				dispatch("changeMult", files);
			} else {
				dispatch("change", files[0]);
			}
		} else {
			lastError = getErrorString();
		}
	}

	function getErrorString(): string {
		const types = fileTypes.split(",");
		let typeString = "";
		if (types.length === 1) {
			typeString = types[0];
		} else {
			const last = types.pop();
			typeString = `${types.join(", ")} or ${last}`;
		}
		return `Please upload a file of type ${typeString}`;
	}
</script>

<Dropzone
	{multiple}
	accept={fileTypes}
	{disabled}
	defaultClass={twMerge("flex aspect-[16/9] h-auto w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed bg-gray-50 object-cover hover:bg-gray-100 focus-visible:outline-primary-500", dragCounter && "bg-gray-100", dropzoneClass)}
	on:dragenter={handleDragEnter}
	on:dragleave={handleDragLeave}
	on:dragover={event => event.preventDefault()}
	on:drop={handleDrop}
	on:change={handleChange}
	on:click
>
	<span class="sr-only">Upload file</span>
	<slot />
</Dropzone>
